import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";
import { Paper, Typography } from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import RedirectingComponent from "src/chamber/components/RedirectingComponent";
import { SearchBar } from "src/chamber/features/dashboard/ui/SearchBar";
import { useState } from "react";
import useGetSampleInspectionFilters from "src/sampleInspection/data-access/useGetSampleInspectionFilters";
import useGetChamberUsers from "src/sampleInspection/data-access/useGetChamberUsers";

type TSampleInspectionProps = {
  isOrgAdmin: boolean;
  isSampleInitiator: boolean;
  activeChamberSampleInspectionsCount: number | null;
  activeOwnSampleCasesCount: number | null;
  countsLoading?: boolean;
};

function SampleInspection(props: TSampleInspectionProps) {
  const { t } = useTranslation();
  const [protocol, setProtocol] = useState("");
  const [gemi, setGemi] = useState("");
  const { data: filterData } = useGetSampleInspectionFilters();
  const { data: chamberUsersData } = useGetChamberUsers();

  const sampleInspectionStatusOptions =
    filterData?.data?.sampleInspectionStatuses
      ?.filter((s: any) => s.id !== 1)
      ?.map((s: any) => ({ label: s.description, value: s.id })) ?? [];

  const sampleCaseStatusOptions =
    filterData?.data?.sampleCompanyCaseStatuses
      ?.filter((s: any) => ![0, 1].includes(s.id))
      ?.map((s: any) => ({ label: s.description, value: s.id })) ?? [];

  const initiatorUserOptions =
    chamberUsersData?.data?.map((u: any) => ({
      label: u.a06_fullname,
      value: u.a06_id,
    })) ?? [];

  const redirectors = [
    ...(props.isSampleInitiator
      ? [
          {
            linkText: t(
              "Υποθέσεις δειγματοληπτικών ελέγχων (όλων των Υ.Γ.Ε.ΜΗ.)",
            ),
            linkTo: "/sampleInspection/sample/initiatorsDashboard",
            badgeCount: null,
            filters: [
              {
                filterName: "statusId",
                filterLabel: t("Κατάσταση"),
                filterOptions: sampleInspectionStatusOptions,
              },
              {
                filterName: "initiatorId",
                filterLabel: t("Δημιουργός δείγματος"),
                filterOptions: initiatorUserOptions,
              },
            ],
          },
        ]
      : []),
    ...(props.isOrgAdmin
      ? [
          {
            linkText: t("Υποθέσεις δειγματοληπτικών ελέγχων της Υ.Γ.Ε.ΜΗ."),
            linkTo: "/sampleInspection/sample/chamberDashboard",
            badgeCount: props.activeChamberSampleInspectionsCount,
            badgeLoading: props.countsLoading,
            filters: [
              {
                filterName: "statusId",
                filterLabel: t("Κατάσταση"),
                filterOptions: sampleInspectionStatusOptions,
              },
            ],
          },
        ]
      : []),
    {
      linkText: t("Έλεγχος επιχειρήσεων που μου έχουν ανατεθεί"),
      linkTo: "/sampleInspection/case/dashboard",
      badgeCount: props.activeOwnSampleCasesCount,
      badgeLoading: props.countsLoading,
      filters: [
        {
          filterName: "statusId",
          filterLabel: t("Κατάσταση ελέγχου"),
          filterOptions: sampleCaseStatusOptions,
        },
      ],
    },
  ];

  return (
    <Paper sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1rem",
        }}
      >
        <Typography
          variant="h5"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FactCheckOutlinedIcon />
          {t("Δειγματοληπτικοί Έλεγχοι")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <SearchBar
          mode="sampleCase"
          protocol={protocol}
          gemi={gemi}
          setProtocol={setProtocol}
          setGemi={setGemi}
        />
      </Box>
      <Box sx={{ padding: "1rem", display: "flex", flexDirection: "column" }}>
        {redirectors.map((redirector) => (
          <RedirectingComponent key={redirector.linkTo} {...redirector} />
        ))}
      </Box>
    </Paper>
  );
}

export default SampleInspection;
